import * as React from "react";
import DataEditor, {
  GridCell,
  GridCellKind,
  EditableGridCell,
  Item,
  isEditableGridCell,
  Rectangle,
  DataEditorRef
} from "@glideapps/glide-data-grid";

import $ from "jquery";


import { useAsyncDataSource } from "@glideapps/glide-data-grid-source";

// @ts-ignore
import { patch, get } from "@rails/request.js";

import { SimpleMenu } from "./headerMenu.ts";
import { useLayer } from "react-laag";

import { useEventListener } from "./utils.tsx";

import { isArray } from "lodash";

import { ColouredLabelRenderer, ColouredLabelCell } from "./cells/ColouredLabelCell.ts";
import { MultiButtonRenderer, MultiButtonCell } from "./cells/MultiButtonCell.ts";

type RailsColumn = {
  id: string;
  title: string;
  kind: "text" | "boolean" | "number" | "row-id" | "custom" | "actions";
  readonly?: boolean;
  width?: number;
  icon?: string;
  sort?: { asc: string, desc: string };
};

interface PagyMetadata {
  page: number;
  next: number | null;
  prev: number | null;
  pages: number,
  count: number
}

type Row = {
  // Could be a string in the case of a composite key or UUID key
  id: number | string;
} & Record<string, any>

type Props = {
  columns: RailsColumn[];
  initialRows: Row[];
  initialPagy: PagyMetadata | null;
  fetchUrl: string;
};

type Label = {
  text: string,
  colour: string
}

type FlashableGridCell = GridCell & {
  flash?: "success" | "error";
}

class ContentCache {
  private cachedContent: Map<number, GridCell[]> = new Map();

  get(col: number, row: number) {
    return this.cachedContent.get(col)?.[row];
  }

  set(col: number, row: number, value: GridCell) {
    let rowCache = this.cachedContent.get(col);
    if (rowCache === undefined) {
      this.cachedContent.set(col, (rowCache = []));
    }
    rowCache[row] = value;
  }

  remove(col: number, row: number) {
    let rowCache = this.cachedContent.get(col);
    if (rowCache !== undefined) {
      delete rowCache[row];
    }
  }
}





type NormalizedColumn = RailsColumn & {
  makeCell: (raw: any) => GridCell;
};

// Take the incoming RailsColumn[] JSON array, and map it to a function
// that can generate a GridCell
function normalizeColumns(cols: RailsColumn[]): NormalizedColumn[] {
  return cols.map((col) => {
    const makeCell = (raw: any): GridCell => {
      switch (col.kind) {
        case "custom":
          let data: Label[];
          // Wrap data in an array
          if (isArray(raw)) {
            data = raw;
          } else {
            data = [raw];
          }
          data = data.map((d) => ({ text: d.text, colour: d.colour }));
          return {
            kind: GridCellKind.Custom,
            data: { labels: data },
            allowOverlay: false,
            readonly: true,
            copyData: ""
          } as ColouredLabelCell;
        case "actions":
          // Might be undefined, so handle gracefully
          if (raw === undefined) {
            return {
              kind: GridCellKind.Text,
              allowOverlay: false,
              displayData: "",
              data: ""
            }
          }
          // console.log(raw);
          return {
            kind: GridCellKind.Custom,
            allowOverlay: false,
            readonly: true,
            copyData: "",
            data: {
              kind: "multi-button-cell",
              // TODO: fix any
              buttons: raw.map((a: any) => ({ title: a.title, url: a.url })),
              backgroundColor: ["transparent", "#6572ffee"],
              color: ["accentColor", "accentFg"],
              borderColor: "#6572ffa0",
              borderRadius: 9,
            },
            themeOverride: {
              baseFontStyle: "700 12px",
            }
          } as MultiButtonCell;
        case "boolean":
          return {
            kind: GridCellKind.Boolean,
            data: Boolean(raw),
            allowOverlay: false,
            readonly: col.readonly ?? false,
          };
        case "row-id":
          return {
            kind: GridCellKind.RowID,
            data: raw?.toString() ?? "",
            allowOverlay: false
          };
        case "number":
          return {
            kind: GridCellKind.Number,
            data: Number(raw ?? 0),
            displayData: raw?.toString() ?? "0",
            allowOverlay: true,
            readonly: col.readonly ?? false,
          };
        default:
          return {
            kind: GridCellKind.Text,
            data: raw?.toString() ?? "",
            displayData: raw?.toString() ?? "",
            allowOverlay: true,
            readonly: col.readonly ?? false,
          };
      }
    };
    return { ...col, makeCell };
  });
}





const ReactDataGridComponent: React.FC<Props> = ({ columns, initialRows, initialPagy, fetchUrl }) => {
  // Ref to the HTML element.
  const ref = React.useRef<DataEditorRef | null>(null);

  // The cache is populated during getCellContent()
  const cache = React.useRef(new ContentCache());

  // Access the latest columns in callbacks
  const colsRef = React.useRef<NormalizedColumn[]>(normalizeColumns(columns));


  // Version mechanism for cache invalidation
  const [version, setVersion] = React.useState(0);

  // Track pages being fetched - a set of numbers
  const fetchingPagesRef = React.useRef<Set<number>>(new Set());




  const [pagesLoaded, setPagesLoaded] = React.useState<Set<number>>(new Set());
  const [pagyInfo, setPagyInfo] = React.useState<PagyMetadata | null>(initialPagy);

  const pageSize = 200;
  const maxConcurrent = 4;

  // Map row number to record ID
  // const recordIds = React.useMemo(() => rows.map(r => r.id), [rows]);

  const numRows = (pagyInfo?.count) || 10_000;

  const getRowData = React.useCallback(
    async (page: number, pageSize: number) => {

      const pagyPage = page + 1;

      console.log("Fetching row data: ", pagyPage);

      try {
        const url = new URL(fetchUrl, window.location.origin);
        const params = url.searchParams;
        params.set("page", String(pagyPage));
        params.set("limit", String(pageSize));
        url.search = params.toString();

        const res = await get(url, { responseKind: "json" });
        if (!res.ok) throw new Error("Failed to fetch data");

        const data: { items: Row[]; pagy: PagyMetadata } = await res.json;

        setPagyInfo(data.pagy);
        setPagesLoaded((prev) => new Set(prev).add(page));

        const rows = data.items;
        return rows;
      } catch (err) {
        console.error("Error fetching page", err);
      }

      return null
      // return range(r[0], r[1]).map(rowIndex => [`1, ${rowIndex}`, `2, ${rowIndex}`]);
    },
  []
  );

  const toCell = React.useCallback(
    (rowData: Row, colIndex: number, rowIndex: number) => {

      let cachedCell = cache.current.get(colIndex, rowIndex);
      if (cachedCell !== undefined) {
        return cachedCell;
      }

      const column: NormalizedColumn = colsRef.current[colIndex];
      const field = column.id;

      const raw = rowData[field];

      // The NormalizedColumn knows how to transform the raw data into a GridCell
      const cell = column.makeCell(raw);

      // TODO: make cache work?

      return cell;

    },
    [version]
  );

  const onEdited = React.useCallback(
    (cell: Item, newCell: EditableGridCell, rowData: Row) => {
      const [colIndex, rowIndex] = cell;

      if (isEditableGridCell(newCell)) {

        // const row = rows[rowIndex];
        const recordId = rowData.id;
        const field = colsRef.current[colIndex].id;

        console.log("RecordID: ", recordId);

        // Fire-and-forget async request
        (async () => {
          // TODO: support per-cell update urls and per-row update urls
          const response = await patch(`${rowData.url}`, {
            body: JSON.stringify({ [field]: newCell.data }),
            responseKind: "json",
            contentType: "application/json",
          });

          if (!response.ok) {
            console.warn("Server-side update failed:", response.statusCode);
            cache.current.remove(colIndex, rowIndex);
            // Roll back on failure
            setVersion(v => v + 1);
            return;
          }
          // newRow[colIndex] = newCell.data;

          const updatedRecord = await response.json;
          updateCacheFromServerResponse(rowIndex, updatedRecord);
        })();

        // Opportunistically update the cache
        cache.current.set(colIndex, rowIndex, {
          ...newCell,
          displayData: typeof newCell.data === "string" ? newCell.data : (newCell as any).displayData,
          lastUpdated: performance.now()
        } as any);

        console.log("New Cell Data: ", newCell.data);

      }

      // return newRow;
      return undefined;
    },
    []
  );


  const asyncDataArgs = useAsyncDataSource<Row>(
    pageSize,
    maxConcurrent,
    getRowData,
    toCell,
    onEdited,
    ref
  );




  // Menu stuff
  const [menu, setMenu] = React.useState<{
      col: number;
      bounds: Rectangle;
      links?: { text: string, url: string }[]
  }>();

  const isOpen = menu !== undefined;

  const { layerProps, renderLayer } = useLayer({
      isOpen,
      auto: true,
      placement: "bottom-end",
      triggerOffset: 2,
      onOutsideClick: () => setMenu(undefined),
      trigger: {
          getBounds: () => ({
              left: menu?.bounds.x ?? 0,
              top: menu?.bounds.y ?? 0,
              width: menu?.bounds.width ?? 0,
              height: menu?.bounds.height ?? 0,
              right: (menu?.bounds.x ?? 0) + (menu?.bounds.width ?? 0),
              bottom: (menu?.bounds.y ?? 0) + (menu?.bounds.height ?? 0),
          }),
      },
  });

  const onHeaderMenuClick = React.useCallback((col: number, bounds: Rectangle) => {
    console.log("Header Menu Click: ", col);
    console.log(colsRef.current[col]);
    let sort = colsRef.current[col].sort;
    let links;
    if (sort) {
      links = [
        { text: "Sort ascending",  url: sort.asc  },
        { text: "Sort descending", url: sort.desc }
      ]
    }
    setMenu({ col, bounds, links });
  }, []);

  const onHeaderClicked = React.useCallback((colIndex: number) => {
      // eslint-disable-next-line no-console
      console.log("Header clicked: ", colIndex);
  }, []);










  // Gets defined again on re-render
  function updateCacheFromServerResponse(rowIndex: number, updatedFields: Record<string, unknown>) {

    for (const [field, value] of Object.entries(updatedFields)) {
      const colIndex = colsRef.current.findIndex(c => c.id === field);
      if (colIndex === -1) continue; // field not displayed in the grid

      const column = colsRef.current[colIndex];

      let cell: GridCell = column.makeCell(value);

      // console.log("Server-Side Update: ", column.id)

      const flashCell: FlashableGridCell = {
        ...cell,
        flash: "success",
        themeOverride: { bgCell: "#c8f7c5" }
      };

      cache.current.set(colIndex, rowIndex, flashCell);
      // setVersion(v => v + 1);

      // Remove flash after 600ms
      setTimeout(() => {
        const current = cache.current.get(colIndex, rowIndex) as FlashableGridCell;
        if (current?.flash === "success") {
          // cache.current.remove(colIndex, rowIndex);
          cache.current.set(colIndex, rowIndex, {
            ...cell,
            themeOverride: undefined,
            flash: undefined,
          } as FlashableGridCell);

          setVersion(v => v + 1);
        }
      }, 600);

    }

    ($('body') as any).toast({
      message: "Updated from server response",
      class: "success",
      displayTime: 1000,
      position: "top right"
    });

    setVersion(v => v + 1);
  }




  const [showSearch, setShowSearch] = React.useState(false);

  useEventListener("keydown", React.useCallback(event => {
    if ((event.ctrlKey || event.metaKey) && event.code === "KeyF") {
      setShowSearch(cv => !cv);
      event.stopPropagation();
      event.preventDefault();
    }
  }, []), window, false, true);


  // const onSearchValueChange =


  return (
    <div style={{ height: "550px" }}>
      <h3>Total Rows: {numRows}</h3>
      <DataEditor
        ref={ref}
        //getCellContent={getCellContent}
        //onCellEdited={onCellEdited}
        columns={columns}
        rows={numRows}
        freezeColumns={3}
        onPaste={true}
        //getCellsForSelection={true}
        showSearch={showSearch}
        onSearchClose={ () => setShowSearch(false) }
        { ...asyncDataArgs }
        rowMarkers="checkbox"
        customRenderers={[ColouredLabelRenderer, MultiButtonRenderer]}
        onHeaderMenuClick={onHeaderMenuClick}
        onHeaderClicked={onHeaderClicked}
      />

      {
        isOpen && renderLayer(
          <SimpleMenu {...layerProps}>
            {/*

              Now the tricky bit - these need to be ransack links for handling sorting.
              It would be great to just render this server-side, then have a ref?

              Or, get the current column in this and just supply the sort links in the columns
              JSON.

            */}

            {menu?.links?.map(link =>
              <div key={link.url}>
                <a
                  href={link.url}
                  onClick={() => setMenu(undefined)}
                >
                  {link.text}
                </a>
              </div>
            )}
          </SimpleMenu>
        )
      }
    </div>
  );
}

export default ReactDataGridComponent;
